import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import AvailabilityGrid, {
  generateDates,
  generateTimeSlots,
  slotKeyToISO,
  isoToSlotKey,
} from '../components/AvailabilityGrid';

const publicApi = axios.create({ baseURL: '/api' });

function fmtDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}
function fmtSlot(iso, tz) {
  return new Date(iso).toLocaleString('en-CA', {
    timeZone: tz || undefined, weekday: 'long', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function AvailHeader({ sub }) {
  return (
    <div className="avail-header">
      <img
        src="/mcg-logo.png"
        alt="MCG Career College"
        className="avail-logo"
        onError={e => {
          e.target.onerror = null;
          e.target.src = '/mcg-emblem.png';
        }}
      />
      <div className="avail-brand">MCG Career College</div>
      {sub && <div className="avail-header-sub">{sub}</div>}
    </div>
  );
}

function StateCard({ icon, iconClass, title, children }) {
  return (
    <div className="avail-page">
      <div className="avail-wrap">
        <AvailHeader />
        <div className="avail-card avail-state-card">
          <div className={`avail-state-icon${iconClass ? ` ${iconClass}` : ''}`}>{icon}</div>
          <h2>{title}</h2>
          <div>{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function AvailabilityPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [errorState, setErrorState] = useState(null);
  const [selected, setSelected] = useState(new Set());     // grid flow
  const [pickedSlot, setPickedSlot] = useState(null);       // pick flow
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);               // pick confirmation

  useEffect(() => {
    publicApi.get(`/public/interview/${token}`)
      .then(res => {
        const s = res.data;
        setSession(s);
        if (s.flow === 'grid') {
          if (s.responded) setDone(true);
          else if (s.my_availability?.length > 0) {
            setSelected(new Set(s.my_availability.map(iso => isoToSlotKey(iso, s.timezone)).filter(Boolean)));
          }
        }
        setLoading(false);
      })
      .catch(err => {
        const status = err.response?.status;
        if (status === 404) setErrorState('not_found');
        else if (status === 410) setErrorState('expired');
        else setErrorState('error');
        setLoading(false);
      });
  }, [token]);

  // ── Grid flow submit (legacy) ──────────────────────────────────────────────
  const handleGridSubmit = useCallback(async () => {
    if (selected.size === 0) { setSubmitError('Please select at least one available time slot.'); return; }
    setSubmitting(true); setSubmitError('');
    try {
      const slots = Array.from(selected).map(key => slotKeyToISO(key, session.timezone));
      await publicApi.post(`/public/interview/${token}`, { availability: slots });
      setDone(true);
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setSubmitting(false); }
  }, [selected, session, token]);

  // ── Pick flow submit ───────────────────────────────────────────────────────
  const handlePick = useCallback(async () => {
    if (!pickedSlot) { setSubmitError('Please choose a time.'); return; }
    setSubmitting(true); setSubmitError('');
    try {
      const res = await publicApi.post(`/public/interview/${token}/pick`, { slot: pickedSlot });
      setResult(res.data);
      setDone(true);
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setSubmitting(false); }
  }, [pickedSlot, token]);

  if (loading) {
    return (
      <div className="avail-page">
        <div className="avail-wrap">
          <AvailHeader />
          <div className="avail-card">
            <div className="skel skel-title" style={{ width: '60%', marginBottom: 10 }} />
            <div className="skel skel-text"  style={{ width: '80%', marginBottom: 20 }} />
            {[0,1,2].map(i => <div key={i} className="skel" style={{ width: '100%', height: 44, borderRadius: 10, marginBottom: 10 }} />)}
          </div>
        </div>
      </div>
    );
  }

  if (errorState === 'not_found') return <StateCard icon="🔗" title="Link Not Found"><p>This scheduling link doesn't exist or has already been used. Please contact your coordinator if you think this is a mistake.</p></StateCard>;
  if (errorState === 'expired')   return <StateCard icon="⏰" title="Link Expired"><p>This scheduling link has expired or the interview is already booked. Please contact your coordinator.</p></StateCard>;
  if (errorState)                 return <StateCard icon="⚠️" title="Something went wrong"><p>Unable to load the scheduling page. Please try again or contact your coordinator.</p></StateCard>;

  // ── Confirmation ───────────────────────────────────────────────────────────
  if (done) {
    if (session?.flow === 'pick' && result) {
      const online = result.meeting_mode === 'online';
      return (
        <StateCard icon="✓" iconClass="avail-state-icon--success" title="Interview Confirmed!">
          <p style={{ marginBottom: 14 }}>
            You're all set for <strong>{fmtSlot(result.booked_slot, session.timezone)}</strong>{' '}
            with <strong>{session.hostOrgName}</strong>.
          </p>
          <div style={{ textAlign: 'left', background: 'var(--bg-sunken, #f4f6f8)', borderRadius: 10, padding: '12px 14px', fontSize: 14, lineHeight: 1.6 }}>
            {online ? (
              <>
                <div><strong>Format:</strong> Online (Google Meet)</div>
                {result.meetLink && <div style={{ wordBreak: 'break-all' }}><strong>Link:</strong> <a href={result.meetLink} target="_blank" rel="noreferrer">{result.meetLink}</a></div>}
              </>
            ) : (
              <>
                <div><strong>Format:</strong> In person</div>
                {session.location && <div><strong>Location:</strong> {session.location}</div>}
              </>
            )}
            <div><strong>Host:</strong> {session.hostOrgName}</div>
            {session.contact_name && <div><strong>Contact:</strong> {session.contact_name}</div>}
            {session.contact_phone && <div><strong>Phone:</strong> <a href={`tel:${session.contact_phone}`}>{session.contact_phone}</a></div>}
            {session.contact_email && <div><strong>Email:</strong> <a href={`mailto:${session.contact_email}`}>{session.contact_email}</a></div>}
          </div>
          <p style={{ marginTop: 16, fontSize: 13, color: 'var(--ink-3)' }}>
            A calendar invitation has been emailed to you{online ? ' with the meeting link' : ''}. You may close this tab.
          </p>
        </StateCard>
      );
    }
    return (
      <StateCard icon="✓" iconClass="avail-state-icon--success" title="Availability Submitted!">
        <p>Thank you — your availability has been recorded.<br />Your coordinator will be in touch once a time is confirmed.</p>
        <div style={{ marginTop: 20, fontSize: 13, color: 'var(--ink-3)' }}>You may close this tab.</div>
      </StateCard>
    );
  }

  // ── PICK flow ──────────────────────────────────────────────────────────────
  if (session.flow === 'pick') {
    const online = session.meeting_mode === 'online';
    return (
      <div className="avail-page">
        <div className="avail-wrap">
          <AvailHeader sub="Interview Scheduling" />
          <div className="avail-card">
            <div className="avail-intro">
              <h1 className="avail-title">Pick your interview time</h1>
              <p className="avail-desc">
                {session.hostOrgName} is ready to meet you! Choose the time that works best for a{' '}
                <strong>{session.slot_minutes}-minute</strong> interview and we'll confirm it right away.
              </p>
            </div>

            <div className="avail-mode-banner">
              {online ? (
                <><span className="avail-mode-ico">🎥</span><div><strong>Online interview</strong><div className="avail-mode-sub">A Google Meet link will be emailed to you once you pick a time.</div></div></>
              ) : (
                <><span className="avail-mode-ico">📍</span><div><strong>In-person interview</strong><div className="avail-mode-sub">{session.location || 'Location will be confirmed by your coordinator.'}</div></div></>
              )}
            </div>

            {(session.contact_name || session.contact_phone || session.contact_email) && (
              <div className="avail-contact">
                <div className="avail-contact-label">Your interview contact</div>
                <div className="avail-contact-body">
                  {session.contact_name && <span><strong>{session.contact_name}</strong></span>}
                  {session.contact_phone && <span> · <a href={`tel:${session.contact_phone}`}>{session.contact_phone}</a></span>}
                  {session.contact_email && <span> · <a href={`mailto:${session.contact_email}`}>{session.contact_email}</a></span>}
                </div>
              </div>
            )}

            <div className="avail-slots">
              {session.offered_slots.map((iso, i) => (
                <button
                  key={i}
                  type="button"
                  className={`avail-slot-btn${pickedSlot === iso ? ' selected' : ''}`}
                  onClick={() => setPickedSlot(iso)}
                >
                  <span className="avail-slot-radio" />
                  <span>{fmtSlot(iso, session.timezone)}</span>
                </button>
              ))}
            </div>

            {submitError && <div className="avail-error">{submitError}</div>}

            <div className="avail-footer">
              <div className="avail-sel-count">{session.timezone}</div>
              <button className="btn btn-primary" style={{ minWidth: 180, justifyContent: 'center', padding: '11px 26px', fontSize: 14 }} onClick={handlePick} disabled={submitting || !pickedSlot}>
                {submitting ? 'Confirming…' : 'Confirm This Time'}
              </button>
            </div>
          </div>
          <div className="avail-page-foot">Powered by MCG Career Services Portal</div>
        </div>
      </div>
    );
  }

  // ── GRID flow (legacy) ─────────────────────────────────────────────────────
  const startStr = session.date_range_start ? new Date(session.date_range_start).toISOString().slice(0, 10) : '';
  const endStr = session.date_range_end ? new Date(session.date_range_end).toISOString().slice(0, 10) : '';
  const numDays = generateDates(startStr, endStr).length;
  const numSlots = generateTimeSlots(session.working_hours_start, session.working_hours_end, session.slot_minutes).length;
  const partyLabel = session.party === 'host'
    ? `Scheduling an interview for ${session.studentFullName}`
    : `Finding a time to interview at ${session.hostOrgName}`;

  return (
    <div className="avail-page">
      <div className="avail-wrap">
        <AvailHeader sub="Interview Scheduling" />
        <div className="avail-card">
          <div className="avail-intro">
            <h1 className="avail-title">{partyLabel}</h1>
            <p className="avail-desc">
              Select every time slot when you're available for a <strong>{session.slot_minutes}-minute</strong> interview.
              The more you select, the easier it is to find a match.
            </p>
          </div>
          <div className="avail-meta-row">
            <div className="avail-meta-item"><span className="avail-meta-label">Date window</span><span className="avail-meta-val">{fmtDate(session.date_range_start)} — {fmtDate(session.date_range_end)}</span></div>
            <div className="avail-meta-item"><span className="avail-meta-label">Working hours</span><span className="avail-meta-val">{session.working_hours_start} – {session.working_hours_end}</span></div>
            <div className="avail-meta-item"><span className="avail-meta-label">Timezone</span><span className="avail-meta-val">{session.timezone}</span></div>
          </div>
          <div className="avail-grid-hint">Click or drag to select / deselect slots &nbsp;·&nbsp; {numDays} days &nbsp;·&nbsp; {numSlots} slots per day</div>
          <AvailabilityGrid
            dateRangeStart={startStr} dateRangeEnd={endStr}
            workingHoursStart={session.working_hours_start} workingHoursEnd={session.working_hours_end}
            slotMinutes={session.slot_minutes} timezone={session.timezone}
            mode="select" selected={selected} onSelectionChange={setSelected}
          />
          {submitError && <div className="avail-error">{submitError}</div>}
          <div className="avail-footer">
            <div className="avail-sel-count">{selected.size === 0 ? 'No slots selected' : `${selected.size} slot${selected.size !== 1 ? 's' : ''} selected`}</div>
            <button className="btn btn-primary" style={{ minWidth: 160 }} onClick={handleGridSubmit} disabled={submitting || selected.size === 0}>
              {submitting ? 'Submitting…' : 'Submit Availability'}
            </button>
          </div>
        </div>
        <div className="avail-page-foot">Powered by MCG Career Services Portal</div>
      </div>
    </div>
  );
}
